import { Fragment, Suspense, useMemo } from 'react';
import { FiberProvider, useContextBridge } from 'its-fine';

import { getDepth } from '@spazious/dollhouse-selector';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { hasOwnProperty } from '@spazious/dollhouse-utils';

import { Annotation } from '../Annotation/index.js';
import { Decoration } from '../Decoration/index.js';
import { ErrorBoundary } from '../ErrorBoundary/index.js';
import { FArea } from '../FArea/index.js';
import { FComposition } from '../FComposition/index.js';
import { FPolygon } from '../FPolygon/index.js';
import { Furniture } from '../Furniture/index.js';
import { Measurement } from '../Measurement/index.js';
import { useStore } from '../StoreProvider/index.js';

export const shallowComponentsMapping: Record<number, React.FC<any>> = {
  [FShallowNodeTypesEnum.FComposition]: FComposition,
  [FShallowNodeTypesEnum.Furniture]: Furniture,
  [FShallowNodeTypesEnum.Decoration]: Decoration,
  [FShallowNodeTypesEnum.FPolygon]: FPolygon,
  [FShallowNodeTypesEnum.FArea]: FArea,
  [FShallowNodeTypesEnum.Measurement]: Measurement,
  [FShallowNodeTypesEnum.Annotation]: Annotation,
};

/**
 * Internal component to render the layout into the graph
 */
const FSetupImpl: React.FC = () => {
  const graph = useStore(({ graph }) => graph);
  const onError = useStore(({ onError }) => onError);

  const children = useMemo(
    () =>
      Object.entries(graph.nodes ?? {}).reduce((acc, [selector, node]) => {
        const { type, ...restProps } = node;
        // Ignore overrides
        if (type === FShallowNodeTypesEnum.FOverride) {
          return acc;
        }
        // We only want to render the first level of nodes
        if (getDepth(selector) !== 1) {
          return acc;
        } else if (!hasOwnProperty(shallowComponentsMapping, type)) {
          throw new Error(`Unknown node type: ${type}`);
        }
        const Component = shallowComponentsMapping[type];

        return [
          ...acc,
          <ErrorBoundary key={selector} onError={onError}>
            <Suspense fallback={null}>
              <Component {...restProps} selector={selector} shallow={node} />
            </Suspense>
          </ErrorBoundary>,
        ];
      }, [] as React.ReactNode[]),
    [graph.nodes, onError]
  );

  return <Fragment>{children}</Fragment>;
};

const FSetupBridge: React.FC = () => {
  const Bridge = useContextBridge();
  return (
    <Bridge>
      <FSetupImpl />
    </Bridge>
  );
};

export const FSetup: React.FC = () => {
  return (
    <FiberProvider>
      <FSetupBridge />
    </FiberProvider>
  );
};
